import { memo, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { useOrganizationUpdateMutation } from 'hooks/mutations/org'
import { environmentsOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Environments'
import { membersRoute } from 'pages/NWPlatform/Organizations/Organization/Members'
import { organizationRoute } from 'pages/NWPlatform/Organizations/Organization/Routing'
import { teamsRoute } from 'pages/NWPlatform/Organizations/Organization/Teams'
import { organizationQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['common', 'pages', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { organizationId } = organizationRoute.useParams()

  const organizationQuery = useQuery(organizationQueryKeys.detail({ organizationId }))
  const { data: organization } = organizationQuery

  const { mutate: updateOrganization, ...organizationUpdateMutation } = useOrganizationUpdateMutation()

  const handleUpdateOrganizationName = useCallback(
    (title: string, closeCallback: () => void) => {
      if (organization) {
        updateOrganization(
          {
            organizationId,
            organizationEdit: {
              name: title,
            },
          },
          { onError: () => enqueueSnackbar(t('pages:My.updateError')), onSettled: closeCallback },
        )
      }
    },
    [t, organization, updateOrganization, organizationId, enqueueSnackbar],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={organizationQuery.data?.name ?? ''}
          isLoading={organizationQuery.isLoading}
          isSaving={organizationUpdateMutation.isPending}
          onSave={handleUpdateOrganizationName}
          TabNavigation={
            <Tabs>
              <NavigationTabLink
                from={organizationRoute.fullPath}
                to={membersRoute.to}
                activeOptions={{ exact: false }}
              >
                {t('entity:org.member', { count: 2 })}
              </NavigationTabLink>
              <NavigationTabLink from={organizationRoute.fullPath} to={teamsRoute.to}>
                {t('entity:org.team', { count: 2 })}
              </NavigationTabLink>
              <NavigationTabLink from={organizationRoute.fullPath} to={environmentsOverviewRoute.to}>
                {t('entity:org.environment', { count: 2 })}
              </NavigationTabLink>
            </Tabs>
          }
        />
      }
    />
  )
}

export const Structure = memo(StructureRaw)
